/**
 * SMS Delivery Templates Page
 *
 * Super Admin settings page for managing global SMS templates used when
 * delivering Document and Form campaigns via SMS. Templates support
 * {{variable}} placeholders resolved at send time in the delivery worker.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { cn, Button } from '@respectly/ui';
import {
  FileText,
  MessageSquare,
  ArrowLeft,
  Plus,
  RefreshCw,
  Star,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileSignature,
  ClipboardList,
} from 'lucide-react';
import type { SmsTemplate, SmsTemplateType, CreateSmsTemplateDto, UpdateSmsTemplateDto } from '@respectly/shared-types';
import {
  useSmsTemplatesStore,
  useDocumentSmsTemplates,
  useFormSmsTemplates,
} from '../../stores/useSmsTemplatesStore';
import { SmsTemplateModal } from '../../components/settings';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, scale: 0.97, transition: { duration: 0.15 } },
};

// ============================================
// Template Card Component
// ============================================

interface TemplateCardProps {
  template: SmsTemplate;
  isSettingDefault: boolean;
  onEdit: (t: SmsTemplate) => void;
  onDelete: (t: SmsTemplate) => void;
  onSetDefault: (t: SmsTemplate) => void;
}

function TemplateCard({
  template,
  isSettingDefault,
  onEdit,
  onDelete,
  onSetDefault,
}: TemplateCardProps) {
  const preview = template.body.length > 90
    ? template.body.slice(0, 87) + '…'
    : template.body;

  return (
    <motion.div
      layout
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={cn(
        'group relative rounded-xl border bg-white dark:bg-slate-900',
        'transition-shadow duration-200 hover:shadow-md',
        template.isDefault
          ? 'border-amber-200 dark:border-amber-800/60 shadow-amber-50 dark:shadow-none'
          : 'border-slate-200 dark:border-slate-700/60'
      )}
    >
      {/* Default accent bar */}
      {template.isDefault && (
        <div className="absolute inset-x-0 top-0 h-0.5 rounded-t-xl bg-gradient-to-r from-amber-400 to-amber-500" />
      )}

      <div className="p-4">
        {/* Top row: name + badges */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="font-semibold text-slate-900 dark:text-white truncate">
                {template.name}
              </span>

              {template.isDefault && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50 shrink-0">
                  <Star className="w-2.5 h-2.5 fill-current" />
                  Default
                </span>
              )}

              {template.isActive ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50 shrink-0">
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700 shrink-0">
                  <XCircle className="w-2.5 h-2.5" />
                  Inactive
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            {!template.isDefault && (
              <button
                onClick={() => onSetDefault(template)}
                disabled={isSettingDefault}
                title="Set as default"
                className={cn(
                  'p-1.5 rounded-lg text-slate-400 transition-colors',
                  'hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-900/20 dark:hover:text-amber-400',
                  isSettingDefault && 'opacity-50 cursor-not-allowed'
                )}
              >
                <Star className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => onEdit(template)}
              title="Edit template"
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(template)}
              title="Delete template"
              className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Body preview */}
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-mono">
          {preview}
        </p>

        {/* Variable chips */}
        {template.variables.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
            {template.variables.map((v) => (
              <span
                key={v}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700"
              >
                {`{{${v}}}`}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// Empty State Component
// ============================================

function EmptyTemplates({
  type,
  onCreate,
}: {
  type: SmsTemplateType;
  onCreate: () => void;
}) {
  const Icon = type === 'document' ? FileSignature : ClipboardList;
  const label = type === 'document' ? 'document' : 'form';

  return (
    <motion.div
      variants={itemVariants}
      className="flex flex-col items-center justify-center py-16 px-8 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-slate-400 dark:text-slate-500" />
      </div>
      <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">
        No {label} SMS templates yet
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mb-6">
        Create an SMS template with dynamic variables to personalise {label} delivery notifications.
      </p>
      <Button onClick={onCreate} size="sm">
        <Plus className="w-4 h-4 mr-2" />
        Create {type === 'document' ? 'Document' : 'Form'} Template
      </Button>
    </motion.div>
  );
}

// ============================================
// Main Component
// ============================================

export function SmsTemplates() {
  const {
    isLoadingList,
    listError,
    isCreating,
    isUpdating,
    isSettingDefault,
    actionError,
    loadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setDefault,
    clearError,
  } = useSmsTemplatesStore();

  const documentTemplates = useDocumentSmsTemplates();
  const formTemplates = useFormSmsTemplates();

  const [activeTab, setActiveTab] = useState<SmsTemplateType>('document');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);

  const currentTemplates = activeTab === 'document' ? documentTemplates : formTemplates;
  const isActionLoading = isCreating || isUpdating;

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleCreate = () => {
    setSelectedTemplate(null);
    setModalOpen(true);
  };

  const handleEdit = (template: SmsTemplate) => {
    setSelectedTemplate(template);
    setModalOpen(true);
  };

  const handleDelete = async (template: SmsTemplate) => {
    if (
      !confirm(
        `Delete "${template.name}"? This action cannot be undone.${template.isDefault ? ' This is the current default template.' : ''}`
      )
    )
      return;
    await deleteTemplate(template.id);
  };

  const handleSetDefault = async (template: SmsTemplate) => {
    await setDefault(template.id);
  };

  const handleSubmit = async (
    data: CreateSmsTemplateDto | UpdateSmsTemplateDto
  ): Promise<boolean> => {
    if (selectedTemplate) {
      return await updateTemplate(selectedTemplate.id, data as UpdateSmsTemplateDto);
    }
    const id = await createTemplate(data as CreateSmsTemplateDto);
    return id !== null;
  };

  const tabs: { value: SmsTemplateType; label: string; icon: React.ElementType; count: number }[] = [
    { value: 'document', label: 'Documents', icon: FileSignature, count: documentTemplates.length },
    { value: 'form', label: 'Forms', icon: ClipboardList, count: formTemplates.length },
  ];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Page Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/settings"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>

            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-teal-200 dark:from-teal-900/40 dark:to-teal-800/40 flex items-center justify-center">
              <FileText className="w-6 h-6 text-teal-600 dark:text-teal-400" />
            </div>

            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                SMS Delivery Templates
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-0.5">
                Configure SMS templates for document and form sending with dynamic variables
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadTemplates()}
              disabled={isLoadingList}
            >
              <RefreshCw
                className={cn('w-4 h-4 mr-2', isLoadingList && 'animate-spin')}
              />
              Refresh
            </Button>
            <Button onClick={handleCreate} disabled={isLoadingList}>
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-6">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150',
                  isActive
                    ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800/60'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50'
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={cn(
                      'inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs font-semibold',
                      isActive
                        ? 'bg-teal-200 dark:bg-teal-800 text-teal-700 dark:text-teal-300'
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Error Banner */}
      {(listError || actionError) && (
        <motion.div
          variants={itemVariants}
          className="mx-6 mt-4 flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 text-sm text-red-700 dark:text-red-400"
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{listError || actionError}</span>
          <button
            onClick={clearError}
            className="ml-auto text-red-500 hover:text-red-700 dark:hover:text-red-300 transition-colors"
          >
            ✕
          </button>
        </motion.div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {isLoadingList ? (
          // Loading skeleton
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-28 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={activeTab}>
              {currentTemplates.length === 0 ? (
                <EmptyTemplates type={activeTab} onCreate={handleCreate} />
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  <AnimatePresence>
                    {currentTemplates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        isSettingDefault={isSettingDefault === template.id}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onSetDefault={handleSetDefault}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Informational footer */}
        {currentTemplates.length > 0 && !isLoadingList && (
          <motion.div
            variants={itemVariants}
            className="mt-6 flex items-start gap-2.5 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700/60 text-sm text-slate-500 dark:text-slate-400"
          >
            <MessageSquare className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <div>
              <span className="font-medium text-slate-600 dark:text-slate-300">
                How templates work:
              </span>{' '}
              The{' '}
              <span className="font-medium text-amber-600 dark:text-amber-400">Default</span>{' '}
              template is used automatically when sending{' '}
              {activeTab === 'document' ? 'document signing' : 'form'} campaigns via SMS. Variables
              like <code className="font-mono text-teal-600 dark:text-teal-400">{'{{firstName}}'}</code>{' '}
              and{' '}
              <code className="font-mono text-teal-600 dark:text-teal-400">
                {activeTab === 'document' ? '{{signingUrl}}' : '{{formUrl}}'}
              </code>{' '}
              are resolved with live data at send time.
            </div>
          </motion.div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SmsTemplateModal
        template={selectedTemplate}
        open={modalOpen}
        onOpenChange={setModalOpen}
        defaultType={activeTab}
        onSubmit={handleSubmit}
        isLoading={isActionLoading}
      />
    </motion.div>
  );
}
